import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { v1URL } from '../../../../../../../shared/constants/urls.constants';

/**
 * Sample-records HTTP service.
 *
 * Wraps the /v1/sample-record CRUD + push endpoints exposed by the api
 * (feature/work-sample-collection branch). All calls go through the shared
 * `CommonService.callAPI`, which handles the `token` auth header and unwraps
 * the HttpResponse body.
 */
@Injectable({
  providedIn: 'root',
})
export class SampleRecordsService implements OnDestroy {
  // Triggered by the wrapper after a successful save/push so the list
  // re-fetches without reaching directly into the list component.
  refresh: Subject<void> = new Subject<void>();

  private readonly onDestroy: Subject<void> = new Subject<void>();

  constructor(private commonService: CommonService) {}

  list(payload: {
    projectId: string;
    start?: number;
    total?: number;
    dailyReportId?: number | string;
    bidItemMaterialSetId?: number | string;
  }): Observable<any> {
    const cfg = v1URL.sampleRecord.list;
    // serializeQueryParams in urls.constants picks up every defined key from `qp`,
    // so the optional dailyReportId/bidItemMaterialSetId scope params flow through
    // without any extra plumbing.
    return this.commonService.callAPI(cfg.method, cfg.url({ qp: payload }));
  }

  fetch(uuid: string): Observable<any> {
    const cfg = v1URL.sampleRecord.fetchByUuid;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }));
  }

  create(payload: any): Observable<any> {
    const cfg = v1URL.sampleRecord.create;
    return this.commonService.callAPI(cfg.method, cfg.url(), payload);
  }

  update(uuid: string, payload: any): Observable<any> {
    const cfg = v1URL.sampleRecord.update;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }), payload);
  }

  delete(uuid: string): Observable<any> {
    const cfg = v1URL.sampleRecord.delete;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }));
  }

  push(uuid: string): Observable<any> {
    const cfg = v1URL.sampleRecord.push;
    return this.commonService.callAPI(cfg.method, cfg.url({ uuid }), {});
  }

  materials(projectId: string, q?: string): Observable<any> {
    const cfg = v1URL.sampleRecord.materials;
    return this.commonService.callAPI(cfg.method, cfg.url({ qp: { projectId, ...(q ? { q } : {}) } }));
  }

  /**
   * Source picker options for the sample-record form, narrowed to sources
   * approved for the picked material per AASHTOWare's SourceMaterials list.
   * Without `materialId` returns the org's full source list; with one,
   * returns only approved sources. Same response shape as the lookup-derived
   * `lookups.org_source_pit` pool the form already consumes.
   */
  sources(
    projectId: string,
    materialId?: number | string,
    sampleDate?: string | null,
  ): Observable<any> {
    const cfg = v1URL.sampleRecord.sources;
    return this.commonService.callAPI(
      cfg.method,
      cfg.url({
        qp: {
          projectId,
          ...(materialId !== undefined && materialId !== null && materialId !== '' ? { materialId } : {}),
          ...(sampleDate ? { sampleDate } : {}),
        },
      }),
    );
  }

  /**
   * Test-method picker options for the sample-record form, narrowed to the
   * test methods with a matching `material_test_requirement` rule for the
   * picked material (and optionally the picked sample_type). Without
   * `materialId` returns the org's full test-method list. Same response
   * shape as the lookup-derived `lookups.org_material_test` pool.
   */
  testMethods(
    projectId: string,
    materialId?: number | string,
    sampleType?: string | null,
  ): Observable<any> {
    const cfg = v1URL.sampleRecord.testMethods;
    return this.commonService.callAPI(
      cfg.method,
      cfg.url({
        qp: {
          projectId,
          ...(materialId !== undefined && materialId !== null && materialId !== '' ? { materialId } : {}),
          ...(sampleType ? { sampleType } : {}),
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
